import { useState, useEffect, useRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  PlayArrow as PlayIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { usePodcastSearchInfinite, usePodcastEpisodesInfinite, useAddEpisodeToProject } from '../../hooks/usePodcasts';
import { useProjects } from '../../hooks/useProjects';
import { FirstEpisodeTutorial } from '../../components/onboarding/FirstEpisodeTutorial';
import type { PodcastEpisode, PodcastSearchResult } from '../../types/api.types';

export const PodcastSearch = () => {
  const { t } = useTranslation('podcasts');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [selectedPodcast, setSelectedPodcast] = useState<PodcastSearchResult | null>(null);
  const [selectedEpisode, setSelectedEpisode] = useState<{
    podcast: PodcastSearchResult;
    episode: PodcastEpisode;
  } | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [episodeSearchTerm, setEpisodeSearchTerm] = useState('');
  const [pendingPlayerUrl, setPendingPlayerUrl] = useState<string | null>(null);
  const [showFirstEpisodePopup, setShowFirstEpisodePopup] = useState(false);
  const [lastAddedEpisodeUuid, setLastAddedEpisodeUuid] = useState<string | null>(null);

  const { 
    data: searchData, 
    isLoading: isSearching, 
    error: searchError,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasNextSearchPage,
    isFetchingNextPage: isFetchingNextSearchPage,
  } = usePodcastSearchInfinite(activeSearchTerm, !!activeSearchTerm);
  
  const { 
    data: episodesData, 
    isLoading: isLoadingEpisodes,
    error: episodesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePodcastEpisodesInfinite(selectedPodcast?.name || '', !!selectedPodcast);
  const { projects } = useProjects();
  const { mutate: addEpisode, isPending: isAdding } = useAddEpisodeToProject();

  // Calculate total episodes across all projects for tutorial
  const totalEpisodeCount = useMemo(() => {
    if (!projects) return 0;
    return projects.reduce((sum, project) => sum + (project.podcastEpisodes?.length || 0), 0);
  }, [projects]);


  // Ref for intersection observer (search results)
  const searchObserverTarget = useRef<HTMLDivElement>(null);
  // Ref for intersection observer (episodes)
  const episodesObserverTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll for search results
  useEffect(() => {
    if (!selectedPodcast) { // Only when viewing search results
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextSearchPage && !isFetchingNextSearchPage) {
            fetchNextSearchPage();
          }
        },
        { threshold: 0.1 }
      );

      const currentTarget = searchObserverTarget.current;
      if (currentTarget) {
        observer.observe(currentTarget);
      }

      return () => {
        if (currentTarget) {
          observer.unobserve(currentTarget);
        }
      };
    }
  }, [hasNextSearchPage, isFetchingNextSearchPage, fetchNextSearchPage, selectedPodcast]);

  // Infinite scroll for episodes
  useEffect(() => {
    if (selectedPodcast) { // Only when viewing episodes
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 }
      );

      const currentTarget = episodesObserverTarget.current;
      if (currentTarget) {
        observer.observe(currentTarget);
      }

      return () => {
        if (currentTarget) {
          observer.unobserve(currentTarget);
        }
      };
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, selectedPodcast]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setActiveSearchTerm(searchTerm.trim());
      setSelectedPodcast(null); // Reset selection when searching
    }
  };

  const handleSelectPodcast = (podcastResult: PodcastSearchResult) => {
    setSelectedPodcast(podcastResult);
    setEpisodeSearchTerm(''); // Reset episode search
  };

  const handleBackToResults = () => {
    setSelectedPodcast(null);
    setEpisodeSearchTerm('');
  };

  const handleAddEpisode = (episode: PodcastEpisode) => {
    if (!selectedPodcast) return;
    setSelectedEpisode({ podcast: selectedPodcast, episode });
  };

  const handleConfirmAdd = () => {
    if (!selectedEpisode || !selectedProjectId || !episodesData?.pages[0]) return;

    const { episode } = selectedEpisode;
    const podcast = episodesData.pages[0]; // Get podcast info from first page
    
    addEpisode(
      {
        projectId: selectedProjectId as number,
        payload: {
          podcast: {
            uuid: podcast.uuid,
            name: podcast.name,
            language: podcast.language,
            rssUrl: podcast.rssUrl,
            imageUrl: podcast.imageUrl,
            websiteUrl: podcast.websiteUrl,
            description: podcast.description,
          },
          episode: {
            uuid: episode.uuid,
            datePublished: episode.datePublished,
            name: episode.name,
            description: episode.description,
            imageUrl: episode.imageUrl,
            subTitle: episode.subTitle,
            audioUrl: episode.audioUrl,
            videoUrl: episode.videoUrl,
            fileLength: episode.fileLength,
            fileType: episode.fileType,
            duration: episode.duration,
            isExplicit: episode.isExplicit,
          },
        },
      },
      {
      onSuccess: (response) => {
        console.log('Episode added successfully:', response);
        console.log('Response podcastId:', response?.podcastId);
        console.log('Selected projectId:', selectedProjectId);
        console.log('Total episodes before add:', totalEpisodeCount);
        
        // Close dialog
        setSelectedEpisode(null);
        setSelectedProjectId('');
        
        // If we got a podcast ID back and this is the first episode, show tutorial
        if (response?.podcastId && selectedProjectId && totalEpisodeCount === 0) {
          const playerUrl = `/player/${selectedProjectId}/podcast/${response.podcastId}`;
          console.log('First episode! Showing tutorial');
          setLastAddedEpisodeUuid(episode.uuid);
          setPendingPlayerUrl(playerUrl);
          setShowFirstEpisodePopup(true);
        } else {
          console.log('Episode added - dialog closed');
        }
      },
      }
    );
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const sanitizeHtml = (html: string) => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  };

  const truncateHtml = (html: string, maxLength: number) => {
    const temp = document.createElement('div');
    temp.innerHTML = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
    const text = temp.textContent || temp.innerText || '';

    if (text.length <= maxLength) {
      return sanitizeHtml(html);
    }

    const truncated = text.substring(0, maxLength);
    temp.textContent = truncated + '...';
    return DOMPurify.sanitize(temp.innerHTML, { ALLOWED_TAGS: [] });
  };

  // Flatten all search results from all pages
  const allSearchResults = searchData?.pages.flatMap(page => page.results) || [];
  const totalSearchCount = searchData?.pages[0]?.totalCount || 0;
  
  // Flatten all episodes from all pages
  const allEpisodes = episodesData?.pages.flatMap(page => page.podcastEpisodes || []) || [];
  
  // Filter episodes based on search term
  const filteredEpisodes = episodeSearchTerm.trim()
    ? allEpisodes.filter(episode => 
        episode.name.toLowerCase().includes(episodeSearchTerm.toLowerCase()) ||
        episode.description.toLowerCase().includes(episodeSearchTerm.toLowerCase())
      )
    : allEpisodes;

  // Get podcast info and total count from first page
  const podcast = episodesData?.pages[0];
  const totalEpisodesCount = podcast?.totalEpisodesCount || 0;
  const loadedEpisodesCount = allEpisodes.length;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('searchPodcasts')}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('searchDescription')}
      </Typography>

      {/* Search Form */}
      <Box component="form" onSubmit={handleSearch} sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder={t('enterPodcastName')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SearchIcon />}
            disabled={!searchTerm.trim() || isSearching}
          >
            {t('search')}
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {isSearching && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {searchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('failedToSearch')}
        </Alert>
      )}

      {/* No Results State */}
      {!isSearching && !selectedPodcast && activeSearchTerm && allSearchResults.length === 0 && !searchError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('noResults', { searchTerm: activeSearchTerm })}
        </Alert>
      )}

      {/* Search Results - Show list of podcasts */}
      {allSearchResults.length > 0 && !isSearching && !selectedPodcast && (
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('searchResults', { loaded: allSearchResults.length, total: totalSearchCount })}
          </Typography>
          <Grid container spacing={2}>
            {allSearchResults.map((result) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={result.uuid}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onClick={() => handleSelectPodcast(result)}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={result.imageUrl || '/placeholder-podcast.png'}
                    alt={result.name}
                    sx={{ objectFit: 'contain', bgcolor: 'action.hover', p: 1 }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom noWrap>
                      {result.name}
                    </Typography>
                    <Chip
                      label={result.language}
                      size="small"
                      color="primary"
                      sx={{ mb: 1 }}
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtml(truncateHtml(result.description || '', 150))
                      }}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Intersection observer target for infinite scroll */}
          <Box ref={searchObserverTarget} sx={{ py: 2, textAlign: 'center' }}>
            {isFetchingNextSearchPage && (
              <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  {t('loadingMorePodcasts')}
                </Typography>
              </Box>
            )}
            {!hasNextSearchPage && allSearchResults.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('allPodcastsLoaded')}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Episodes View - Show when podcast is selected */}
      {selectedPodcast && (
        <Box>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBackToResults}
            sx={{ mb: 2 }}
          >
            {t('backToResults')}
          </Button>

          {episodesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('failedToLoadEpisodes')}
            </Alert>
          )}

          {podcast && (
        <Grid container spacing={3}>
          {/* Podcast Info */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ position: 'sticky', top: 16 }}>
              <CardMedia
                component="img"
                height="300"
                image={podcast.imageUrl || '/placeholder-podcast.png'}
                alt={podcast.name}
                sx={{ objectFit: 'contain', bgcolor: 'action.hover', p: 1 }}
              />
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {podcast.name}
                </Typography>
                <Chip
                  label={podcast.language}
                  size="small"
                  color="primary"
                  sx={{ mb: 2 }}
                />
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  paragraph
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(podcast.description || '') }}
                />
                {podcast.websiteUrl && (
                  <Button
                    size="small"
                    href={podcast.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('visitWebsite')}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Episodes List */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6">
                {t('episodesOfTotal', { loaded: loadedEpisodesCount, total: totalEpisodesCount })}
              </Typography>
              
              {/* Episode Search/Filter */}
              <TextField
                size="small"
                placeholder={t('searchEpisodes')}
                value={episodeSearchTerm}
                onChange={(e) => setEpisodeSearchTerm(e.target.value)}
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: episodeSearchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setEpisodeSearchTerm('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {isLoadingEpisodes && allEpisodes.length === 0 ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : filteredEpisodes.length > 0 ? (
              <>
                <List>
                  {filteredEpisodes.map((episode) => (
                    <Card
                      key={episode.uuid}
                      sx={{ mb: 2 }}
                      {...(episode.uuid === lastAddedEpisodeUuid
                        ? { 'data-tour': 'first-added-episode' }
                        : undefined)}
                    >
                      <ListItem
                        sx={{
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: 'flex-start',
                        }}
                      >
                        {episode.imageUrl && (
                          <CardMedia
                            component="img"
                            sx={{ width: 100, height: 100, mr: 2, borderRadius: 1 }}
                            image={episode.imageUrl}
                            alt={episode.name}
                          />
                        )}
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" component="span">
                                {episode.name}
                              </Typography>
                              {episode.isExplicit && (
                                <Chip label={t('explicit')} size="small" color="warning" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                paragraph
                                dangerouslySetInnerHTML={{ 
                                  __html: sanitizeHtml(truncateHtml(episode.description || '', 200))
                                }}
                              />
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(episode.datePublished)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t('duration', { duration: formatDuration(episode.duration) })}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
                          <IconButton
                            color="primary"
                            onClick={() => handleAddEpisode(episode)}
                          >
                            <AddIcon />
                          </IconButton>
                          {episode.audioUrl && (
                            <IconButton
                              href={episode.audioUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <PlayIcon />
                            </IconButton>
                          )}
                        </Box>
                      </ListItem>
                    </Card>
                  ))}
                </List>

                {/* Intersection observer target for infinite scroll */}
                {!episodeSearchTerm && (
                  <Box ref={episodesObserverTarget} sx={{ py: 2, textAlign: 'center' }}>
                    {isFetchingNextPage && (
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          {t('loadingMorePodcasts')}
                        </Typography>
                      </Box>
                    )}
                    {!hasNextPage && loadedEpisodesCount > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('allEpisodesLoaded')}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {episodeSearchTerm 
                    ? t('noEpisodesMatching', { searchTerm: episodeSearchTerm })
                    : t('noEpisodesFound')
                  }
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
          )}
        </Box>
      )}

      {/* Add to Project Dialog */}
      <Dialog
        open={!!selectedEpisode}
        onClose={() => setSelectedEpisode(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('addEpisodeToProject')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('selectProjectPrompt', { episodeName: selectedEpisode?.episode.name })}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('selectProject')}</InputLabel>
            <Select
              value={selectedProjectId}
              label={t('selectProject')}
              onChange={(e) => setSelectedProjectId(e.target.value as number)}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.projectName} ({project.projectLanguage})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEpisode(null)}>{t('cancel')}</Button>
          <Button
            onClick={handleConfirmAdd}
            variant="contained"
            disabled={!selectedProjectId || isAdding}
          >
            {isAdding ? <CircularProgress size={24} /> : t('addToProjectButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* First Episode Tutorial */}
      <FirstEpisodeTutorial
        open={showFirstEpisodePopup}
        onComplete={() => {
          setShowFirstEpisodePopup(false);
          if (pendingPlayerUrl) {
            navigate(pendingPlayerUrl);
            setPendingPlayerUrl(null);
          }
        }}
        onSkip={() => {
          setShowFirstEpisodePopup(false);
          setPendingPlayerUrl(null);
        }}
        playerUrl={pendingPlayerUrl}
      />
    </Box>
  );
};
